import {
  createUserWithEmailAndPassword,
  OAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
} from "firebase/auth";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { auth } from "../firebase/config";
import type { AuthContextType, AuthUser } from "../types";

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AUTHORIZED_USERS: readonly string[] = [
  "alyssa.filippelli@lullaboo.ca",
  "maykel.faragalla@lullaboo.ca",
  "tiffany.tang@lullaboo.ca",
  "mina.jeldi@lullaboo.ca",
  "chelsea.rogers@lullaboo.ca",
  "nina.truong@lullaboo.ca",
];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const signup = (email: string, password: string): Promise<UserCredential> => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email: string, password: string): Promise<UserCredential> => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = (): Promise<void> => {
    return signOut(auth);
  };

  const loginWithMicrosoft = async (): Promise<User> => {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({
      tenant: import.meta.env.VITE_MICROSOFT_TENANT_ID || "common",
    });

    try {
      const result = await signInWithPopup(auth, provider);
      console.log("Microsoft sign-in successful:", result.user.email);

      if (result.user.email && AUTHORIZED_USERS.includes(result.user.email))
        return result.user;
      else {
        await signOut(auth);
        throw new Error(
          "Unauthorized: You do not have access to this application."
        );
      }
    } catch (error: any) {
      console.error("Firebase Microsoft sign-in error:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);

      // Handle specific Firebase auth errors
      switch (error.code) {
        case "auth/popup-closed-by-user":
          throw new Error("Sign-in was cancelled.");
        case "auth/popup-blocked":
          throw new Error(
            "Popup was blocked. Please allow popups for this site and try again."
          );
        case "auth/account-exists-with-different-credential":
          throw new Error(
            "An account already exists with the same email but different sign-in method."
          );
        case "auth/network-request-failed":
          throw new Error(
            "Network error. Please check your connection and try again."
          );
        case "auth/internal-error":
          throw new Error(
            "Microsoft sign-in is not properly configured. Please ensure the Microsoft provider is enabled in Firebase Console and has valid credentials."
          );
        case "auth/unauthorized-domain":
          throw new Error(
            `This domain (${window.location.origin}) is not authorized. Please add it to Firebase Console > Authentication > Settings > Authorized domains.`
          );
        default:
          throw new Error(
            `Microsoft sign-in failed: ${error.message || "Please try again."}`
          );
      }
    }
  };

  useEffect(() => {
    console.log("AuthContext: Initializing Firebase auth...");
    console.log("AuthContext: Environment variables check:");
    console.log(
      "VITE_FIREBASE_API_KEY:",
      import.meta.env.VITE_FIREBASE_API_KEY ? "SET" : "MISSING"
    );
    console.log(
      "VITE_FIREBASE_AUTH_DOMAIN:",
      import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ? "SET" : "MISSING"
    );
    console.log(
      "VITE_FIREBASE_PROJECT_ID:",
      import.meta.env.VITE_FIREBASE_PROJECT_ID ? "SET" : "MISSING"
    );

    try {
      const unsubscribe = onAuthStateChanged(
        auth,
        (user: User | null) => {
          console.log(
            "AuthContext: Auth state changed:",
            user ? "User logged in" : "No user"
          );
          if (user) {
            const authUser: AuthUser = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
            };
            setCurrentUser(authUser);
          } else {
            setCurrentUser(null);
          }
          setLoading(false);
          setError(null);
        },
        (error: any) => {
          console.error("AuthContext: Firebase auth error:", error);
          setError(error.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error: any) {
      console.error("AuthContext: Firebase initialization error:", error);
      setError(error.message);
      setLoading(false);
      return () => {}; // Return empty function to satisfy all code paths requirement
    }
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    error,
    signup,
    login,
    loginWithMicrosoft,
    logout,
  };

  // Show error if Firebase failed to initialize
  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Firebase Configuration Error</h2>
        <p>Error: {error}</p>
        <p>
          Please check your Firebase configuration and environment variables.
        </p>
        <p>Make sure you have set up the following environment variables:</p>
        <ul style={{ textAlign: "left", maxWidth: "500px", margin: "0 auto" }}>
          <li>VITE_FIREBASE_API_KEY</li>
          <li>VITE_FIREBASE_AUTH_DOMAIN</li>
          <li>VITE_FIREBASE_PROJECT_ID</li>
          <li>VITE_FIREBASE_STORAGE_BUCKET</li>
          <li>VITE_FIREBASE_MESSAGING_SENDER_ID</li>
          <li>VITE_FIREBASE_APP_ID</li>
        </ul>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
